import { useEffect, useState } from 'react'

/**
 * React Hook for CSP Nonce Management
 * Provides secure nonce for inline scripts and styles
 */

interface CSPNonceData {
  nonce: string
  timestamp: number
}

export const useCSPNonce = (): {
  nonce: string
  isLoaded: boolean
  getNonceAttr: () => { nonce?: string }
  addNonceToScript: (scriptElement: HTMLScriptElement) => void
} => {
  const [nonce, setNonce] = useState<string>('')
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  useEffect(() => {
    // Try to get nonce from meta tag first
    const metaTag = document.querySelector('meta[http-equiv="Content-Security-Policy"]')
    if (metaTag) {
      const content = metaTag.getAttribute('content') || ''
      const nonceMatch = content.match(/nonce-([A-Za-z0-9+/=]+)/)
      if (nonceMatch) {
        setNonce(nonceMatch[1])
        setIsLoaded(true)
        return
      }
    }

    // Fallback: try to load from nonce file (for build environments)
    fetch('/csp-nonce.json')
      .then(response => response.json())
      .then((data: CSPNonceData) => {
        if (data.nonce) {
          setNonce(data.nonce)
        }
      })
      .catch(() => {
        // Fallback: generate client-side nonce (less secure but functional)
        // eslint-disable-next-line no-console
        console.warn('CSP nonce not found, generating client-side fallback')
        const fallbackNonce = btoa(Math.random().toString()).replace(/[^A-Za-z0-9]/g, '').substring(0, 16)
        setNonce(fallbackNonce)
      })
      .finally(() => {
        setIsLoaded(true)
      })
  }, [])

  // Helper to create nonce attribute for inline scripts/styles
  const getNonceAttr = () => nonce ? { nonce } : {}

  // Helper to add nonce to existing script elements
  const addNonceToScript = (scriptElement: HTMLScriptElement) => {
    if (nonce && !scriptElement.src) {
      scriptElement.nonce = nonce
    }
  }

  return {
    nonce,
    isLoaded,
    getNonceAttr,
    addNonceToScript
  }
}

// Utility function to create CSP-compliant inline scripts
export const createCSPScript = (code: string, nonce?: string): HTMLScriptElement => {
  const script = document.createElement('script')
  script.textContent = code
  if (nonce) {
    script.nonce = nonce
  }
  return script
}

// Global CSP violation reporter
export const reportCSPViolation = (violation: SecurityPolicyViolationEvent): void => {
  // Filter out iframe-src violations from reporting
  if (violation.violatedDirective === 'frame-src' || violation.violatedDirective === 'iframe-src') {
    return
  }

  // eslint-disable-next-line no-console
  console.warn('🛡️ CSP Violation Detected:', {
    directive: violation.violatedDirective,
    blockedURI: violation.blockedURI,
    documentURI: violation.documentURI,
    sourceFile: violation.sourceFile,
    lineNumber: violation.lineNumber,
    columnNumber: violation.columnNumber,
    originalPolicy: violation.originalPolicy
  })

  // In production, you might want to send this to your monitoring service
  if (process.env.NODE_ENV === 'production') {
    // Example: Send to monitoring service
    // fetch('/api/csp-violations', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     directive: violation.violatedDirective,
    //     blockedURI: violation.blockedURI,
    //     sourceFile: violation.sourceFile,
    //     lineNumber: violation.lineNumber
    //   })
    // }).catch(() => {})
  }
}